import * as React from 'react'
import {
  Button,
  Checkbox,
  Form,
  Typography,
  ConfirmationModal,
  TextField,
  Snackbar,
  Loader,
  Paper,
  Grid,
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import { FormikTextField, CenteredContainer } from 'components'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import * as Yup from 'yup'
import qs from 'qs'
import { useAuth, useAuthApi, useLogger, useFormFactor } from 'hooks'
import { LoginViewModel, FedLoginViewModel, SendFedCodeViewModel } from '../api'
import { useState } from 'react'
import clsx from 'clsx'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      boxShadow: '0px 1px 2px rgba(0, 0, 0,0.06)',
      backgroundColor: theme.palette.background.paper,
    },
    root: {
      flexGrow: 1,
      minHeight: 0,
    },
    centeredContent: {
      [theme.breakpoints.down('sm')]: {
        margin: 0,
      },
    },
    form: {
      padding: theme.spacing(8),
      width: 300,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    formItem: {
      // we need 35px between form items, but the helper text takes up 12
      marginTop: 23,
      width: '100%',
    },
    loaderContainer: {
      width: '100%',
    },
    firstFormItem: {
      width: '100%',
    },
    buttonBar: {
      width: '100%',
      marginTop: theme.spacing(8),
    },
    associatedFormItem: {},
    confirmationModalFormItem: {
      // since we don't have a field above this, set it to the full 35px
      marginTop: 35,
      marginBottom: 35,
    },
    actionAndLoader: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    loader: {
      flexGrow: 1,
    },
  }),
)

const LoginFormSchema = Yup.object().shape({
  username: Yup.string().required('The Username field is required.'),
  password: Yup.string().required('The Password field is required.'),
  domainName: Yup.string(),
  agencyId: Yup.string().required('The Agency ID field is required.'),
  rememberMe: Yup.boolean(),
})

type MfaState = {
  verificationCode: string
  lastAttemptFailed: boolean
  inProgress: boolean
}

const AgencyIdLocalStorageKey = 'FieldOps::AgencyId'
const AgencyExternalIdLocalStorageKey = 'FieldOps::AgencyExternalId'
const FederationVerifierLocalStorageKey = 'FieldOps::Verifier'
const FederationTokenKeysStorageKey = 'FieldOps::TokenKeys'
const FederationLogoutEndpointKey = 'FieldOps::LogoutEndpoint'

export const Login = () => {
  const classes = useStyles({})
  const { dispatch: dispatchAuthAction } = useAuth()
  const { logError } = useLogger()
  const formFactor = useFormFactor()

  const {
    login,
    checkIsFederated,
    sendFedCode,
    getCurrentSession,
    sendVerificationCode,
    submitVerificationCode,
  } = useAuthApi()

  const [mfaState, setMfaState] = useState<MfaState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isFedAuthLoading, setFedAuthLoading] = useState<boolean>(false)
  const [hasCheckedFederation, setCheckedFederation] = useState<boolean>(false)

  const rememberedAgencyId = localStorage.getItem(AgencyIdLocalStorageKey)

  const initialValues: LoginViewModel = {
    username: '',
    password: '',
    domainName: '',
    agencyId: rememberedAgencyId !== null ? rememberedAgencyId : '',
    rememberMe: rememberedAgencyId !== null,
  }

  const paramString =
    window.location.search != ''
      ? window.location.search
      : window.location.hash.replace('/', '').replace('#', '')
  const queryString = qs.parse(paramString, {
    ignoreQueryPrefix: true,
  })
  const stateFromURL = queryString.state as string

  const onVerificationCodeChanged = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const prevState = mfaState as MfaState
    setMfaState({
      ...prevState,
      verificationCode: e.target.value,
      inProgress: false,
    })
  }

  React.useEffect(() => {
    const checkCode = async () => {
      if (!stateFromURL) return
      const agencyExternalId = localStorage.getItem(
          AgencyExternalIdLocalStorageKey,
      )
      const codeVerifier = localStorage.getItem(
        FederationVerifierLocalStorageKey,
      )
      const storedKeysString = localStorage.getItem(
        FederationTokenKeysStorageKey,
      )
      const tokenDict = {} as { [key: string]: string }
      if (storedKeysString) {
        const tokenKeys = storedKeysString.split(',')
        tokenKeys.forEach((element) => {
          const valString = queryString[element] as string
          if (valString) {
            tokenDict[element] = valString
          }
        })
      }
      const sendValues: SendFedCodeViewModel = {
        agencyExternalId: agencyExternalId !== null ? agencyExternalId : '',
        originatingUrl: window.location.href,
        savedState: stateFromURL,
        fedKeyValues: tokenDict,
        codeVerifier: codeVerifier !== null ? codeVerifier : '',
      }

      localStorage.removeItem(AgencyExternalIdLocalStorageKey)
      localStorage.removeItem(FederationVerifierLocalStorageKey)
      localStorage.removeItem(FederationTokenKeysStorageKey)
      const stateVars = stateFromURL.toString().split(',')
      const originalPath =
        stateVars.length >= 2 ? decodeURIComponent(stateVars[1]) : ''

      // If the user cancels the login from the IdP, we won't have a code or
      // proper state/path to continue so we want to redirect to the root page.
      if (originalPath == '') {
        window.location.replace('/')
        return
      }

      setFedAuthLoading(true)
      sendFedCode(sendValues)
        .then((response) => {
          if (response.status === 200) {
            if (response.data.isAuthenticated) {
              // load and set the current session
              return getCurrentSession(dispatchAuthAction, originalPath)
            }
          }
          if (localStorage.getItem(AgencyIdLocalStorageKey) !== null) {
            localStorage.removeItem(AgencyIdLocalStorageKey)
          }
          setFedAuthLoading(false)
          setError('There was an error validating your login')
          window.location.replace(originalPath)
        })
        .catch((error) => {
          logError(error)
          if (localStorage.getItem(AgencyIdLocalStorageKey) !== null) {
            localStorage.removeItem(AgencyIdLocalStorageKey)
          }
          setFedAuthLoading(false)
          setError('There was an error submitting your request')
        })
    }

    checkCode()
  }, [])

  React.useEffect(() => {
    checkFederatedAuth(initialValues)
  }, [])

  const submitMfaVerificationCode = () => {
    if (mfaState === null) return

    setMfaState({
      ...mfaState,
      inProgress: true,
    })

    submitVerificationCode({
      provider: 'Phone',
      code: mfaState.verificationCode,
      returnUrl: '/',
      rememberMe: false,
      rememberBrowser: false,
    })
      .then((response) => {
        if (response.status === 200 && response.data.isSuccess) {
          return getCurrentSession(dispatchAuthAction)
        } else {
          setError('There was an error verifying your code')

          setMfaState({
            lastAttemptFailed: true,
            verificationCode: '',
            inProgress: false,
          })
        }
      })
      .catch((error) => {
        logError(error)

        setError('There was an error verifying your code')

        setMfaState({
          lastAttemptFailed: true,
          verificationCode: '',
          inProgress: false,
        })
      })
  }

  const checkFederatedAuth = (values: LoginViewModel) => {
    if (!values.agencyId || stateFromURL) return
    // Send an API request to check if federation should be used
    const fedCheckInput: FedLoginViewModel = {
      agencyName: values.agencyId,
      originatingUrl: window.location.href,
    }
    checkIsFederated(fedCheckInput)
      .then((response) => {
        if (response.status === 200) {
          setCheckedFederation(true)
          if (response.data.useFederatedAuthentication) {
            if (values.rememberMe) {
              localStorage.setItem(AgencyIdLocalStorageKey, values.agencyId)
            } else if (
              !values.rememberMe &&
              localStorage.getItem(AgencyIdLocalStorageKey) !== null
            ) {
              localStorage.removeItem(AgencyIdLocalStorageKey)
            }
            localStorage.setItem(
                AgencyExternalIdLocalStorageKey,
                response.data.agencyExternalId,
            )
            localStorage.setItem(
              FederationVerifierLocalStorageKey,
              response.data.codeVerifier,
            )
            localStorage.setItem(
              FederationTokenKeysStorageKey,
              response.data.tokenKeys.join(),
            )

            if (response.data.logoutUrl !== null) {
              localStorage.setItem(
                FederationLogoutEndpointKey,
                response.data.logoutUrl,
              )
            }
            window.location.assign(response.data.authProviderUrl)
          } else {
            // Remove any previous values stored for federated auth.
            // We remove any previously stored logout endpoint for the
            // scenario where a user logs in with a fed auth agency,
            // then later logs in with a non-fed auth agency we don't
            // want the "Sign Out" button to redirect them to log out
            // the previous fed auth agency.
            // This isn't common, but with "tritech" (non-fed auth) and
            // "cs" (fed auth) being used back to back this can occur.
            localStorage.removeItem(FederationLogoutEndpointKey)
          }
        } else {
          setError('There was an error submitting your request')
        }
      })
      .catch((error) => {
        logError(error)
        setError('There was an error submitting your request')
      })
  }

  const sendMfaCode = () => {
    // tell the server to send the MFA code
    sendVerificationCode({
      selectedProvider: 'Phone',
      rememberMe: false,
      returnUrl: '/',
      providers: [],
    })
      .then((mfaResponse) => {
        if (mfaResponse.status === 200 && mfaResponse.data.isSuccess) {
          // reset the local MFA state
          setMfaState({
            verificationCode: '',
            lastAttemptFailed: false,
            inProgress: false,
          })
        } else {
          setError('There was an error generating your verification code')
        }
      })
      .catch((error) => {
        logError(error)
        setError('There was an error generating your verification code')
      })
  }

  const performLogin = (
    values: LoginViewModel,
    { resetForm, setSubmitting }: any,
  ) => {
    if (!hasCheckedFederation) {
      checkFederatedAuth(values)
    }
    login(values)
      .then((response) => {
        // TODO: if the server returns field level validation, then render that
        if (response.status === 200) {
          if (response.data.requiresTwoFactorAuthentication) {
            // if they checked the remember me box then save that
            if (values.rememberMe) {
              localStorage.setItem(AgencyIdLocalStorageKey, values.agencyId)
            } else if (
              !values.rememberMe &&
              localStorage.getItem(AgencyIdLocalStorageKey) !== null
            ) {
              localStorage.removeItem(AgencyIdLocalStorageKey)
            }

            sendMfaCode()

            // wipe the input fields
            resetForm()

            setSubmitting(false)
          } else if (response.data.isAuthenticated) {
            // if they checked the remember me box then save that
            if (values.rememberMe) {
              localStorage.setItem(AgencyIdLocalStorageKey, values.agencyId)
            } else if (
              !values.rememberMe &&
              localStorage.getItem(AgencyIdLocalStorageKey) !== null
            ) {
              localStorage.removeItem(AgencyIdLocalStorageKey)
            }

            // load and set the current session
            return getCurrentSession(dispatchAuthAction)
          } else if (response.data.errorMessage) {
            setError(response.data.errorMessage)
            setSubmitting(false)
          } else {
            setError('There was an error submitting your request')
            setSubmitting(false)
          }
        } else {
          setError('There was an error submitting your request')
          setSubmitting(false)
        }
      })
      .catch((error) => {
        setSubmitting(false)

        logError(error)
        setError('There was an error submitting your request')
      })
  }

  const checkIfSigningIn = (
    isLoginSubmitting: boolean,
    isFedAuthLoading: boolean,
  ) => isLoginSubmitting || isFedAuthLoading

  return (
    <React.Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={LoginFormSchema}
        onSubmit={performLogin}
      >
        {(formikProps) => (
          <CenteredContainer
            className={classes.root}
            disableVerticalGutters={
              formFactor.isPhone &&
              !checkIfSigningIn(formikProps.isSubmitting, isFedAuthLoading)
            }
            contentClassName={classes.centeredContent}
          >
            <Paper className={classes.paper}>
              <Form
                onSubmit={formikProps.handleSubmit}
                className={classes.form}
              >
                <Grid
                  container
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                >
                  {!checkIfSigningIn(
                    formikProps.isSubmitting,
                    isFedAuthLoading,
                  ) && (
                    <React.Fragment>
                      <Grid item className={classes.firstFormItem}>
                        <FormikTextField
                          formikProps={formikProps}
                          fieldName="agencyId"
                          label="Agency ID"
                          required
                          onBlur={(e) => checkFederatedAuth(formikProps.values)}
                          onChange={(e) => {
                            setCheckedFederation(false)
                            formikProps.handleChange(e)
                          }}
                        />
                      </Grid>
                      <Grid item className={classes.associatedFormItem}>
                        <Checkbox
                          label="Remember Agency ID"
                          id="rememberMe"
                          checked={formikProps.values.rememberMe}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                      <Grid item className={classes.formItem}>
                        <FormikTextField
                          formikProps={formikProps}
                          fieldName="domainName"
                          label="Domain"
                        />
                      </Grid>
                      <Grid item className={classes.formItem}>
                        <FormikTextField
                          formikProps={formikProps}
                          fieldName="username"
                          label="Username"
                          required
                        />
                      </Grid>
                      <Grid item className={classes.formItem}>
                        <FormikTextField
                          formikProps={formikProps}
                          fieldName="password"
                          label="Password"
                          type="password"
                          required
                        />
                      </Grid>
                    </React.Fragment>
                  )}
                  {checkIfSigningIn(
                    formikProps.isSubmitting,
                    isFedAuthLoading,
                  ) && (
                    <Grid item className={classes.loaderContainer}>
                      <Loader
                        size="sm"
                        text="Signing In"
                        disableVerticalGutters
                        className={classes.loader}
                      />
                    </Grid>
                  )}
                  {!checkIfSigningIn(
                    formikProps.isSubmitting,
                    isFedAuthLoading,
                  ) && (
                    <Grid
                      item
                      className={clsx(
                        classes.buttonBar,
                        classes.actionAndLoader,
                      )}
                    >
                      <Button type="submit" color="primary">
                        Sign In
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Form>
            </Paper>
          </CenteredContainer>
        )}
      </Formik>
      <ConfirmationModal
        onCancel={() => setMfaState(null)}
        onConfirm={submitMfaVerificationCode}
        open={mfaState !== null}
        confirmButtonColor="primary"
        confirmText="Sign In"
        isConfirmDisabled={!mfaState?.verificationCode}
        tertiaryActionText="Resend Code"
        tertiaryButtonColor="primary"
        tertiaryButtonVariant="outlined"
        onTertiaryClick={sendMfaCode}
        title="Sign In"
        message={
          <React.Fragment>
            <Typography>
              We sent a verification code to the phone number associated with
              your user account.
            </Typography>
            <TextField
              id="mfaVerificationCode"
              label="Verification Code"
              onChange={onVerificationCodeChanged}
              value={mfaState?.verificationCode}
              className={classes.confirmationModalFormItem}
              error={mfaState?.lastAttemptFailed}
              helperText={
                mfaState?.lastAttemptFailed
                  ? 'Verification code is incorrect'
                  : null
              }
            />
            {mfaState?.inProgress && (
              <Loader size="sm" text="Verifying Code" disableVerticalGutters />
            )}
          </React.Fragment>
        }
      />
      <Snackbar
        severity="error"
        open={error !== null}
        onClose={() => setError(null)}
      >
        {error}
      </Snackbar>
    </React.Fragment>
  );
}
